/** @module What the person sees of the camera picture, and how that maps back to it. */
import type { Rect } from "@/features/scan/guide-frame";

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

export interface View {
  /** 1 shows the whole picture, 2 half of it, and so on. */
  zoom: number;
  /** Middle of the visible cut-out, in fractions of the picture. */
  x: number;
  y: number;
}

export const WHOLE: View = { zoom: 1, x: 0.5, y: 0.5 };

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** The visible part of the camera picture, in fractions of it. */
export function visible(view: View): Rect {
  const size = 1 / clamp(view.zoom, MIN_ZOOM, MAX_ZOOM);
  const half = size / 2;
  return {
    x: clamp(view.x, half, 1 - half) - half,
    y: clamp(view.y, half, 1 - half) - half,
    width: size,
    height: size,
  };
}

/** Zoom in or out around the current middle, keeping the cut-out inside the picture. */
export function zoomed(view: View, zoom: number): View {
  const next = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  const half = 1 / next / 2;
  return { zoom: next, x: clamp(view.x, half, 1 - half), y: clamp(view.y, half, 1 - half) };
}

/** Move the cut-out; the numbers are fractions of what is on screen, not of the picture. */
export function panned(view: View, dx: number, dy: number): View {
  const size = 1 / clamp(view.zoom, MIN_ZOOM, MAX_ZOOM);
  const half = size / 2;
  return {
    ...view,
    x: clamp(view.x - dx * size, half, 1 - half),
    y: clamp(view.y - dy * size, half, 1 - half),
  };
}

/**
 * A rectangle drawn on screen, expressed in the camera picture.
 *
 * Everything the detector and the photo use goes through here: what you see is what
 * gets measured, whatever the zoom is.
 */
export function toPicture(view: View, rect: Rect): Rect {
  const cut = visible(view);
  return {
    x: cut.x + rect.x * cut.width,
    y: cut.y + rect.y * cut.height,
    width: rect.width * cut.width,
    height: rect.height * cut.height,
  };
}

/** The CSS transform that shows exactly that cut-out inside a clipping box. */
export function transformOf(view: View): string {
  const cut = visible(view);
  const shiftX = (0.5 - (cut.x + cut.width / 2)) * 100 * view.zoom;
  const shiftY = (0.5 - (cut.y + cut.height / 2)) * 100 * view.zoom;
  return `scale(${view.zoom}) translate(${shiftX / view.zoom}%, ${shiftY / view.zoom}%)`;
}

/**
 * The frame with a little room around it: what a photo should show.
 *
 * A card that lies slightly askew still belongs in the picture, and the recognition
 * wants a bit of table around the card to find its edges.
 */
export const PHOTO_MARGIN = 0.12;

export function withMargin(rect: Rect, margin = PHOTO_MARGIN): Rect {
  const dx = rect.width * margin;
  const dy = rect.height * margin;
  const x = clamp(rect.x - dx, 0, 1);
  const y = clamp(rect.y - dy, 0, 1);
  return {
    x,
    y,
    width: Math.min(rect.width + 2 * dx, 1 - x),
    height: Math.min(rect.height + 2 * dy, 1 - y),
  };
}

/** Where the frame sits inside a photo of that larger rectangle. */
export function insideOf(outer: Rect, inner: Rect): Rect {
  return {
    x: (inner.x - outer.x) / outer.width,
    y: (inner.y - outer.y) / outer.height,
    width: inner.width / outer.width,
    height: inner.height / outer.height,
  };
}
